/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-organization-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import React, { useState } from 'react';

import { useBoolean, useTranslation } from 'hooks';
import { ADD_ORGANISATION, GET_SYSTEM_ORGANISATIONS } from 'queries/org-admin';
import NotificationService from 'utils/notification';
import { validateFields, checkIfNameExist } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import OutlinedWrapper from 'views/components/core/outlined-wrapper';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';
import Enums from 'views/pages/admin/audit-log/definitions/enums';

import './styles.scss';

interface AddOrganizationDialogInterface {
    open: boolean;
    close(action?: string): any;
    organizations?: any[];
}

const AddOrganizationDialog = ({ open, close, organizations }: AddOrganizationDialogInterface) => {
    const { t } = useTranslation();
    const initialState = { name: '', region: '', loginCode: '', connection: '', useTransactions: false };
    const initialError = { name: '', region: '', loginCode: '', connection: '' };
    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({ ...initialError });
    const disabledSaveButton = useBoolean(true);
    const [saveOrganisation] = useMutation(ADD_ORGANISATION);

    const onClose = (action?: string) => {
        disabledSaveButton.setTrue();
        setState({ ...initialState });
        setErrors({ ...initialError });
        close(action);
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        let error: any = {};
        if (field === 'connection') {
            error = { connection: '' };
            if (value?.toLowerCase().indexOf('mongodb') !== 0) error.connection = t('admin:itShouldBeValidMongoDbLink');
        } else if (field === 'loginCode') {
            error = { loginCode: '' };
            const regexp = /^[A-Za-z0-9-_]+$/;
            if (value?.length && !regexp.test(value)) error.loginCode = t('admin:pathShouldBeValid');
            else error = validateFields(value, field, length, required);
        } else error = validateFields(value, field, length, required);

        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (fieldName: string, length?: number, required?: boolean) => (event: any) => {
        const { value } = event.target;
        if (disabledSaveButton.value) disabledSaveButton.setFalse();
        setState({ ...state, [fieldName]: value });

        validate(value, fieldName, length, required);
    };

    const checkRequiredFields = (fields: string[]) => {
        let isValid = true;
        let newErrors = { ...errors };
        fields.forEach((field) => {
            const error = validateFields((state as any)[field], field, 0, true);
            newErrors = {
                ...newErrors,
                ...error,
            };
        });
        if (Object.keys(newErrors).filter((item) => (newErrors as any)[item]).length > 0) {
            isValid = false;
        }
        setErrors({ ...newErrors });
        return isValid;
    };

    const checkForErrors = () => Object.keys(errors).filter((item) => (errors as any)[item]).length > 0;

    const successRequest = () => {
        NotificationService.showNotification(t('admin:organizationSuccessfulCreate'));
        onClose('add');
    };

    const errorRequest = (errorMessage?: string) => {
        NotificationService.showNotification(errorMessage ?? t('errorMessage'));
        disabledSaveButton.setFalse();
    };

    const checkForName = () => {
        const value: any = {
            ...state,
            name: state.name.trim(),
            loginCode: state.loginCode.trim(),
            id: null,
        };
        const resultName = checkIfNameExist(value, organizations, 'name');
        const resultURLPath = checkIfNameExist(value, organizations, 'loginCode');
        if (resultName) setErrors({ ...errors, name: t('admin:orgAlreadyExists') });
        else if (resultURLPath) setErrors({ ...errors, loginCode: t('admin:pathAlreadyExists') });

        return resultName || resultURLPath;
    };

    const submit = () => {
        const requiredFields = ['name', 'region', 'loginCode'];
        if (state.region === 'CUSTOM') requiredFields.push('connection');
        if (!checkForErrors() && checkRequiredFields(requiredFields) && !checkForName()) {
            disabledSaveButton.setTrue();
            saveOrganisation({
                variables: {
                    name: state.name,
                    region: state.region,
                    urlPath: state.loginCode,
                    connectionString: state.connection ?? '',
                    useTransactions: state.useTransactions,
                },
                refetchQueries: [
                    {
                        query: GET_SYSTEM_ORGANISATIONS,
                    },
                ],
            })
                .then((response: any) => {
                    if (response.data.addOrganisation === null) successRequest();
                    else errorRequest(response.data.addOrganisation);
                })
                .catch(() => {
                    errorRequest();
                });
        }
    };

    const onCheckboxChange = (fieldName: string) => () => {
        setState({ ...state, [fieldName]: !(state as any)[fieldName] });
    };

    const getRegionsList = () => {
        const newRegionList = Enums.Regions.map((region: string) => ({
            id: region,
            text: t(`admin:${region}`),
            default: false,
        }));
        newRegionList.unshift({ id: '', text: t('admin:selectDbRegion'), default: true });
        return newRegionList;
    };

    const regionsList = getRegionsList();

    return (
        <>
            {open && (
                <CustomDialog
                    onClose={() => {
                        onClose();
                    }}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                    open={open}
                >
                    <DialogTitle id="add-organization-modal-title">
                        {t('admin:addOrganizationConfiguration')}
                        <FontAwesomeIcon
                            icon={['fal', 'times']}
                            className="fa-lg close-icon-cv"
                            onClick={() => {
                                onClose();
                            }}
                            data-testid="close-dialog"
                        />
                    </DialogTitle>
                    <DialogContent className="add-organization-dialog-content">
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <TextField
                                    label={`${t('admin:organization')}*`}
                                    id="add-organization-dialog-name"
                                    fullWidth
                                    value={state.name}
                                    onChange={onChange('name', 150)}
                                    placeholder={`${t('admin:enterName')}*`}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-organization-dialog-name-help-text"
                                            length={state.name?.length}
                                            max={150}
                                            error={errors.name}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.name}
                                    className="before-select"
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type not-full">
                                <SelectWithCategories
                                    showWarning={false}
                                    customClassName="add-organization-region"
                                    fieldSelect="region"
                                    label={`${t('admin:DBRegion')}*`}
                                    filterable={false}
                                    currentState={state.region}
                                    onChange={onChange}
                                    customId="add-organization-region"
                                    dataArray={regionsList}
                                    error={errors.region}
                                />
                            </FormControl>
                        </div>
                        {state.region === 'CUSTOM' && (
                            <OutlinedWrapper label={t('admin:customDatabaseConnection')}>
                                <div className="checkboxes-wrapper">
                                    <div className="left-part">
                                        <Checkbox
                                            rounded="none"
                                            id="crf-assessment-checkbox"
                                            className="cell-checkbox mr no-border"
                                            checked={state.useTransactions}
                                            onChange={onCheckboxChange('useTransactions')}
                                            label={t('admin:useTransactions')}
                                            data-testid="crf-assessment-checkbox"
                                            size="medium"
                                        />
                                    </div>
                                </div>

                                <TextField
                                    id="add-organization-dialog-connection"
                                    label={`${t('admin:connectionString')}*`}
                                    fullWidth
                                    value={state.connection}
                                    onChange={onChange('connection', 4000)}
                                    size="small"
                                    placeholder={`${t('admin:enterTheConnectionString')}*`}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-organization-dialog-connection-help-text"
                                            length={state.connection.length}
                                            max={4000}
                                            error={errors.connection}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.connection}
                                />
                            </OutlinedWrapper>
                        )}
                        <TextField
                            id="add-organization-dialog-path"
                            label={t('admin:URLPath')}
                            fullWidth
                            value={state.loginCode}
                            onChange={onChange('loginCode', 20)}
                            size="small"
                            placeholder={t('admin:enterTheUrlPath')}
                            helperText={
                                <CharactersRemaining
                                    id="add-organization-dialog-path-help-text"
                                    length={state.loginCode.length}
                                    max={20}
                                    error={errors.loginCode}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            required
                            error={!!errors.loginCode}
                        />
                        <DialogActions>
                            <Button
                                disabled={
                                    disabledSaveButton.value ||
                                    Object.keys(errors).filter((item) => (errors as any)[item]).length > 0
                                }
                                className="add-organization-btn"
                                text="add"
                                onClick={submit}
                            />
                        </DialogActions>
                    </DialogContent>
                </CustomDialog>
            )}
        </>
    );
};

export default AddOrganizationDialog;
